<template>
  <v-card dark elevation="0" max-width="900px" class="mx-auto mb-15 mt-5">
    <v-toolbar flat color="primary" dark>
      <v-toolbar-title class="font-weight-bold">Vertragseinstellungen</v-toolbar-title>
      <v-spacer></v-spacer>

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon color="green lighten-2" @click="$router.push('/dashboard/contract/v2')" v-bind="attrs" v-on="on">
            <v-icon>mdi-vector-link</v-icon>
          </v-btn>
        </template>
        <span>Vertragsplattform V2 oeffnen</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon color="orange lighten-2" @click="showPriceUpdateDialog = true" v-bind="attrs" v-on="on">
            <v-icon>mdi-cash-fast</v-icon>
          </v-btn>
        </template>
        <span>Preise 2026 anwenden</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon @click="$router.push('/dashboard/contract/create')" v-bind="attrs" v-on="on">
            <v-icon>mdi-note-plus-outline</v-icon>
          </v-btn>
        </template>
        <span>Neuen Vertrag erstellen</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon @click="showCopyDialog = true" v-bind="attrs" v-on="on">
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
        <span>Einstellungen von Studio kopieren</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon @click="showBackupOptions = !showBackupOptions" v-bind="attrs" v-on="on">
            <v-icon>mdi-database-cog-outline</v-icon>
          </v-btn>
        </template>
        <span>Backup & Wiederherstellung</span>
      </v-tooltip>
    </v-toolbar>

    <v-expand-transition>
      <div v-show="showBackupOptions">
        <v-card-text class="grey darken-3">
          <v-row align="center">
            <v-col cols="12" md="4">
              <v-btn block color="blue-grey" @click="downloadBackup">
                <v-icon left>mdi-download</v-icon>
                Backup
              </v-btn>
            </v-col>
            <v-col cols="12" md="4">
              <div class="input-wrapper" @dragover.prevent @drop.prevent="handleDrop" @click="$refs.fileInput.click()">
                <v-icon>mdi-upload</v-icon>
                <label for="jsonFile" class="input-label ml-2">Restore</label>
                <input id="jsonFile" type="file" class="input-hidden" ref="fileInput" @change="importJson" />
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn block color="teal" @click="cleanupPackages" :loading="cleanupLoading" v-bind="attrs" v-on="on">
                    <v-icon left>mdi-broom</v-icon>
                    Aufräumen
                  </v-btn>
                </template>
                <span>Bereinigt die Paket-Datenstruktur und reduziert die Dateigrösse. (Einmalig ausführen)</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card-text>
      </div>
    </v-expand-transition>

    <v-tabs v-model="tab" background-color="transparent" color="primary" grow>
      <v-tab v-for="item in tabItems" :key="item.key">
        <v-icon left>{{ item.icon }}</v-icon>
        {{ item.name }}
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" class="transparent">
      <v-tab-item v-for="item in tabItems" :key="item.key">
        <v-card flat color="transparent">
          <v-card-text>
            <settings-section
              :title="item.name"
              :items="getCollection(item.key)"
              :headers="getHeaders(item.key)"
              @add="addItem(item.singularKey)"
              @edit="editItem(item.singularKey, $event)"
              @delete="deleteItem(item.singularKey, $event)"
              @rename="openRenameDialog($event)"></settings-section>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <membership-editor
      :visible.sync="membershipDialog"
      :item="editedItem"
      :studios="studios"
      :packages="packages"
      @save="saveItem"
      @update-packages="handlePackagePriceUpdate"></membership-editor>
    <package-editor :visible.sync="packageDialog" :item="editedItem" :studios="studios" :memberships="memberships" @save="saveItem"></package-editor>
    <edit-dialog :visible.sync="genericDialog" :item="editedItem" :type="editedType" :studios="studios" @save="saveItem"></edit-dialog>

    <rename-membership-dialog
      :visible.sync="showRenameDialog"
      :membership="itemToRename"
      :existing-memberships="memberships"
      @rename="handleRenameMembership"></rename-membership-dialog>

    <copy-settings-dialog :visible.sync="showCopyDialog" :studios="studios" @copy="handleCopySettings"></copy-settings-dialog>

    <v-dialog v-model="showConfirmationDialog" persistent max-width="500px">
      <v-card dark>
        <v-card-title class="text-h5 warning--text"><v-icon left color="warning">mdi-alert-outline</v-icon>Bestätigung erforderlich</v-card-title>
        <v-card-text class="white--text mt-4" v-if="editedItem">
          Die Änderungen an der Mitgliedschaft <strong>"{{ editedItem.title }}"</strong> wirken sich auf folgende Zusatzpakete aus:
          <v-list dense class="mt-3" dark>
            <v-list-item v-for="pkg in affectedPackages" :key="pkg"
              ><v-list-item-icon><v-icon>mdi-package-variant</v-icon></v-list-item-icon
              ><v-list-item-content>{{ pkg }}</v-list-item-content></v-list-item
            >
          </v-list>
          Möchtest du fortfahren?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelSave">Abbrechen</v-btn>
          <v-btn color="warning" text @click="confirmAndSaveChanges">Ja, speichern</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPriceUpdateDialog" max-width="600" scrollable>
      <v-card dark>
        <v-card-title class="headline warning--text">Preise auf 2026 aktualisieren?</v-card-title>
        <v-card-text class="pt-4" style="max-height: 60vh">
          <p>Dies führt folgende Aktionen aus:</p>
          <ul>
            <li>Iteriert über alle Mitgliedschaften.</li>
            <li>Ignoriert Studios mit Code: <strong>pfungen, winterthur</strong>.</li>
            <li>Ignoriert: "Black", "Flex", "Rate".</li>
            <li>Setzt neue Preise für Kombi, Group, Wellness & Premium (inkl. Plus-Varianten).</li>
          </ul>
          <v-alert type="error" outlined class="mt-4">
            <strong>Achtung:</strong> Die Änderungen überschreiben die aktuellen Preise in den Einstellungen!
          </v-alert>
          <div v-if="priceUpdatePreview.length > 0" class="mt-3">
            <div class="d-flex justify-space-between align-center mb-2">
              <small>Vorschau ({{ priceUpdatePreview.length }} Änderungen):</small>
            </div>
            <div style="background: #222; padding: 10px; border-radius: 4px; border: 1px solid #444">
              <div v-for="(log, i) in priceUpdatePreview" :key="i" class="caption grey--text text--lighten-1 mb-1">
                {{ log }}
              </div>
            </div>
          </div>
          <div v-else-if="previewCalculated" class="mt-3 text-center grey--text">Keine Änderungen gefunden basierend auf den Kriterien.</div>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="blue lighten-2" @click="calculatePricePreview"> <v-icon left>mdi-eye</v-icon> Vorschau berechnen </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="showPriceUpdateDialog = false">Abbrechen</v-btn>
          <v-btn color="orange" @click="apply2026Prices" :disabled="priceUpdatePreview.length === 0"> Anwenden & Speichern </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import SettingsSection from "./components/SettingsSection.vue"
import EditDialog from "./components/EditDialog.vue"
import MembershipEditor from "./components/MembershipEditor.vue"
import PackageEditor from "./components/PackageEditor.vue"
import RenameMembershipDialog from "./components/RenameMembershipDialog.vue"
import CopySettingsDialog from "./components/CopySettingsDialog.vue"

export default {
  components: { SettingsSection, EditDialog, MembershipEditor, PackageEditor, RenameMembershipDialog, CopySettingsDialog },
  data: () => ({
    tab: null,
    tabItems: [
      { name: "Vertragspartner", key: "companies", singularKey: "company", icon: "mdi-domain" },
      { name: "Kategorien", key: "categories", singularKey: "category", icon: "mdi-view-list" },
      { name: "Mitgliedschaften", key: "memberships", singularKey: "membership", icon: "mdi-star-circle" },
      { name: "Zusatzpakete", key: "packages", singularKey: "package", icon: "mdi-package-variant-plus" },
      { name: "Zahlungsoptionen", key: "paymentOptions", singularKey: "paymentOption", icon: "mdi-credit-card-outline" },
    ],
    headers: {
      companies: [
        { text: "Name", value: "name" },
        { text: "Stadt", value: "city" },
        { text: "Aktionen", value: "actions", sortable: false, align: "end" },
      ],
      categories: [
        { text: "Titel", value: "title" },
        { text: "Aktivierungs-Tarif", value: "noSignUpFee" },
        { text: "Aktionen", value: "actions", sortable: false, align: "end" },
      ],
      memberships: [
        { text: "Titel", value: "title" },
        { text: "Typ", value: "flexActive" },
        { text: "Aktionen", value: "actions", sortable: false, align: "end" },
      ],
      packages: [
        { text: "Titel", value: "title" },
        { text: "Upgrade freigeschaltet", value: "showAsUpgradePossibility" },
        { text: "Anteilsmässige Berechnung", value: "calculateWithEndDate" },
        { text: "Anteilsmässige Berechnung (mind. 6 Monate)", value: "calculateWithEndDateSixMonths" },
        { text: "Aktionen", value: "actions", sortable: false, align: "end" },
      ],
      paymentOptions: [
        { text: "Titel", value: "title" },
        { text: "Aktionen", value: "actions", sortable: false, align: "end" },
      ],
    },
    userService,
    studios: [],
    companies: [],
    categories: [],
    memberships: [],
    packages: [],
    paymentOptions: [],
    editedItem: null,
    originalItem: null,
    originalItemTitle: null,
    editedType: null,
    genericDialog: false,
    membershipDialog: false,
    packageDialog: false,
    showConfirmationDialog: false,
    affectedPackages: [],
    pendingSave: null,
    showBackupOptions: false,
    showRenameDialog: false,
    itemToRename: null,
    cleanupLoading: false,
    showCopyDialog: false,
    // NEU: Variablen für das Preis-Update
    showPriceUpdateDialog: false,
    priceUpdatePreview: [],
    previewCalculated: false,
  }),
  created() {
    this.fetchStudios()
    this.fetchContractSettings()
  },
  methods: {
    getStudioName(studioId) {
      const studio = this.studios.find((s) => s._id === studioId)
      return studio ? studio.name : "Unbekannt"
    },
    // NEU: Hilfsfunktion um Studio Code sicher zu holen
    getStudioCode(studioId) {
      const studio = this.studios.find((s) => s._id === studioId)
      // Fallback auf Name, falls Code leer, sicherheitshalber lowercase
      return studio && studio.code ? studio.code.toLowerCase() : ""
    },

    // NEU: Logik für Preis-Update 2026
    calculatePricePreview() {
      this.priceUpdatePreview = []
      this.runPriceLogic(true) // true = isPreview
      this.previewCalculated = true
    },

    async apply2026Prices() {
      const affectedCount = this.runPriceLogic(false) // false = apply changes

      if (affectedCount > 0) {
        try {
          const loadingToast = this.$toast.info("Speichere neue Preise...", { timeout: false })
          await api.saveContractSettings({ data: this.memberships }, "memberships")
          this.$toast.dismiss(loadingToast)
          this.$toast.success(`${affectedCount} Mitgliedschaften erfolgreich aktualisiert!`)
          this.showPriceUpdateDialog = false
          await this.fetchContractSettings()
        } catch (e) {
          this.$toast.error("Fehler beim Speichern der neuen Preise.")
          console.error(e)
        }
      } else {
        this.$toast.info("Keine Änderungen zum Speichern.")
      }
    },

    runPriceLogic(isPreview) {
      const exceptionStudios = ["pfungen", "winterthur"]
      const logs = []
      let affectedCount = 0

      // Durch alle Memberships loopen
      this.memberships.forEach((membership) => {
        const title = (membership.title || "").toLowerCase()
        let modifiedInThisMembership = false

        // Filter: Black / Flex / Rate ignorieren
        if (title.includes("black") || title.includes("flex") || title.includes("rate")) {
          return
        }

        // Durch alle Studio-Einstellungen dieser Membership loopen
        // membership.studioSettings ist: { [studioId]: { durations: [...] } }
        if (membership.studioSettings) {
          for (const [studioId, settings] of Object.entries(membership.studioSettings)) {
            const studioCode = this.getStudioCode(studioId)

            // Exception Check anhand Code
            if (exceptionStudios.includes(studioCode)) continue
            if (!settings || !settings.durations) continue

            settings.durations.forEach((duration) => {
              let newPrice = null
              const years = parseInt(duration.years) || 0
              const oldPrice = duration.price

              // --- PREIS LOGIK ---

              // Kombi
              if (title.includes("kombi")) {
                if (years === 1) newPrice = 699
                // bei 2 Jahren keine Änderung definiert in Vorlage
                else if (years !== 2) newPrice = 519 // else Fall (vermutlich < 1 Jahr)
              }

              // Group
              else if (title.includes("group")) {
                if (years === 2) newPrice = 1099
                else if (years === 1) {
                  /* kein Change defined for 1 year group in snippet? */
                } else newPrice = 459
              }

              // Wellness
              else if (title.includes("wellness")) {
                if (title.includes("plus")) {
                  if (years === 1) newPrice = 759
                  else if (years === 2) newPrice = 1359
                  else newPrice = 599
                } else {
                  // Wellness normal
                  if (years === 1) newPrice = 659
                  else if (years === 2) newPrice = 1199
                  else newPrice = 499
                }
              }

              // Premium
              else if (title.includes("premium")) {
                if (title.includes("plus")) {
                  if (years === 1) newPrice = 1469
                  else if (years === 2) newPrice = 2789
                  else newPrice = 1099
                } else {
                  // Premium normal
                  if (years === 1) newPrice = 1269
                  else if (years === 2) newPrice = 2399
                  else newPrice = 899
                }
              }

              // Wenn ein neuer Preis gefunden wurde und er anders ist als der alte
              if (newPrice !== null && newPrice !== oldPrice) {
                if (isPreview) {
                  logs.push(`${membership.title} (${studioCode.toUpperCase()} - ${years}J): ${oldPrice} -> ${newPrice}`)
                } else {
                  // Vue 2 Reactivity Fix: $set nutzen
                  this.$set(duration, "price", newPrice)
                  modifiedInThisMembership = true
                }
              }
            })
          }
        }

        if (modifiedInThisMembership && !isPreview) {
          affectedCount++
        }
      })

      if (isPreview) this.priceUpdatePreview = logs
      return affectedCount
    },

    async handleCopySettings({ fromStudioId, toStudioId }) {
      if (!fromStudioId || !toStudioId) {
        this.$toast.error("Bitte Quell- und Zielstudio auswählen.")
        return
      }

      this.$toast.info("Einstellungen werden kopiert, dies kann einen Moment dauern...")

      const collectionsToUpdate = ["companies", "categories", "memberships", "packages", "paymentOptions"]
      const loadingToast = this.$toast.info("Verarbeite Daten...", { timeout: false })

      for (const collectionName of collectionsToUpdate) {
        const collection = JSON.parse(JSON.stringify(this[collectionName]))

        collection.forEach((item) => {
          if (item.studios && item.studios.includes(fromStudioId)) {
            if (!item.studios.includes(toStudioId)) {
              item.studios.push(toStudioId)
            }

            if ((collectionName === "memberships" || collectionName === "packages") && item.studioSettings && item.studioSettings[fromStudioId]) {
              item.studioSettings[toStudioId] = JSON.parse(JSON.stringify(item.studioSettings[fromStudioId]))
            }
          }
        })

        this[collectionName] = collection

        try {
          await api.saveContractSettings({ data: this[collectionName] }, collectionName)
        } catch (e) {
          this.$toast.dismiss(loadingToast)
          this.$toast.error(`Fehler beim Speichern von ${collectionName}. Vorgang abgebrochen.`)
          await this.fetchContractSettings()
          return
        }
      }

      this.$toast.dismiss(loadingToast)
      this.$toast.success(
        `Alle Einstellungen von "${this.getStudioName(fromStudioId)}" wurden erfolgreich auf "${this.getStudioName(toStudioId)}" kopiert.`,
      )
      this.showCopyDialog = false
      await this.fetchContractSettings()
    },
    async fetchStudios() {
      this.studios = await api.fetchStudios()
    },
    async fetchContractSettings() {
      const s = await api.fetchContractSettings()
      if (s) {
        Object.assign(this, s)
      }
    },
    getCollection(key) {
      return (this[key] || []).filter((item) => item !== null && typeof item === "object")
    },
    getHeaders(key) {
      return this.headers[key] || []
    },
    addItem(type) {
      const schemas = {
        company: { name: "Neu", studios: [] },
        category: { title: "Neu", studios: [] },
        membership: { title: "Neu", studios: [], studioSettings: {} },
        package: { title: "Neu", studios: [], studioSettings: {} },
        paymentOption: { title: "Neu", studios: [] },
      }
      this.editItem(type, schemas[type])
    },
    editItem(type, item) {
      this.editedType = type
      this.originalItem = item
      this.editedItem = JSON.parse(JSON.stringify(item))
      this.originalItemTitle = item.title || item.name
      if (type === "membership") this.membershipDialog = true
      else if (type === "package") this.packageDialog = true
      else this.genericDialog = true
    },
    openRenameDialog(item) {
      this.itemToRename = item
      this.showRenameDialog = true
    },
    async handleRenameMembership({ oldTitle, newTitle }) {
      const membershipToUpdate = this.memberships.find((m) => m.title === oldTitle)
      if (membershipToUpdate) {
        membershipToUpdate.title = newTitle
      }

      this.packages.forEach((pkg) => {
        for (const studioId in pkg.studioSettings) {
          const studioSettings = pkg.studioSettings[studioId]
          if (studioSettings && studioSettings.memberships) {
            const affectedMembership = studioSettings.memberships.find((m) => m.title === oldTitle)
            if (affectedMembership) {
              affectedMembership.title = newTitle
            }
          }
        }
      })

      try {
        await api.saveContractSettings({ data: this.memberships }, "memberships")
        await api.saveContractSettings({ data: this.packages }, "packages")
        this.$toast.success(`Mitgliedschaft erfolgreich in "${newTitle}" umbenannt.`)
      } catch (error) {
        this.$toast.error("Fehler beim Umbenennen der Mitgliedschaft.")
        this.fetchContractSettings()
      }
      this.showRenameDialog = false
    },
    async saveItem({ item, type }) {
      if (!item) {
        this.$toast.error("Speichern fehlgeschlagen: Kein Element ausgewählt.")
        return
      }

      const collectionNameMap = {
        company: "companies",
        category: "categories",
        membership: "memberships",
        package: "packages",
        paymentOption: "paymentOptions",
      }
      const collectionName = collectionNameMap[type]
      if (!collectionName) {
        this.$toast.error("Unbekannter Typ kann nicht gespeichert werden.")
        return
      }

      const itemName = (item.title || item.name || "").trim().toLowerCase()

      if (!itemName) {
        this.$toast.error("Der Name darf nicht leer sein.")
        return
      }

      const isDuplicate = this[collectionName].some(
        (i) => (i.title || i.name || "").toLowerCase() === itemName && (i.title || i.name) !== this.originalItemTitle,
      )

      if (isDuplicate && collectionName !== "companies" && collectionName !== "categories") {
        this.$toast.error(`Ein Eintrag mit dem Namen "${item.title || item.name}" existiert bereits.`)
        return
      }

      if (type === "membership" && this.originalItemTitle !== "Neu") {
        const affected = this.findAffectedPackages(item)
        if (affected.length > 0) {
          this.affectedPackages = affected
          this.pendingSave = { item, type }
          this.showConfirmationDialog = true
          this.membershipDialog = false
          return
        }
      }
      await this.performSave(item, type)
    },
    async performSave(item, type) {
      if (!item) return
      const collectionNameMap = {
        company: "companies",
        category: "categories",
        membership: "memberships",
        package: "packages",
        paymentOption: "paymentOptions",
      }
      const collectionName = collectionNameMap[type]

      const index = this[collectionName].indexOf(this.originalItem)

      if (index > -1) {
        // Vue 2 Reactivity Fix: $set für das Haupt-Item
        this.$set(this[collectionName], index, item)
      } else {
        this[collectionName].push(item)
      }

      try {
        await api.saveContractSettings({ data: this[collectionName] }, collectionName)
        this.$toast.success(`${type} erfolgreich gespeichert.`)

        await this.fetchContractSettings()

        if (type === "membership") {
          await this.synchronizePackages(item)
        }
      } catch (e) {
        this.$toast.error("Fehler beim Speichern")
      }

      this.genericDialog = this.membershipDialog = this.packageDialog = false
      this.pendingSave = null
      this.editedItem = null
      this.originalItem = null
      this.originalItemTitle = null
    },
    findAffectedPackages(updatedMembership) {
      if (!updatedMembership) return []
      const affected = new Set()
      ;(this.packages || []).forEach((pkg) => {
        for (const studioId in pkg.studioSettings) {
          if (pkg.studioSettings[studioId]?.memberships?.some((m) => m.title === updatedMembership.title)) {
            affected.add(pkg.title)
          }
        }
      })
      return Array.from(affected)
    },
    async confirmAndSaveChanges() {
      if (this.pendingSave) {
        await this.performSave(this.pendingSave.item, this.pendingSave.type)
      }
      this.showConfirmationDialog = false
    },
    cancelSave() {
      this.showConfirmationDialog = false
      this.pendingSave = null
      this.editedItem = null
      this.originalItem = null
    },
    async deleteItem(type, itemToDelete) {
      if (!itemToDelete) {
        this.$toast.error("Löschen fehlgeschlagen: Kein Element ausgewählt.")
        return
      }
      if (confirm(`Möchtest du "${itemToDelete.title || itemToDelete.name}" wirklich löschen?`)) {
        const collectionNameMap = {
          company: "companies",
          category: "categories",
          membership: "memberships",
          package: "packages",
          paymentOption: "paymentOptions",
        }
        const collectionName = collectionNameMap[type]

        this[collectionName] = this[collectionName].filter((i) => (i.title || i.name) !== (itemToDelete.title || itemToDelete.name))
        try {
          await api.saveContractSettings({ data: this[collectionName] }, collectionName)
          this.$toast.success("Erfolgreich gelöscht")
          if (type === "membership") {
            await this.synchronizePackages(itemToDelete, true)
          }
        } catch (e) {
          this.$toast.error("Fehler beim Löschen")
          this.fetchContractSettings()
        }
      }
    },
    // OPTIMIERUNG: Bessere Vue 2 Reaktivität mit $set statt tiefer Kopien wo möglich
    async synchronizePackages(updatedMembership, isDelete = false) {
      if (!updatedMembership) return

      let changesMade = false

      // Wir iterieren direkt über this.packages und nutzen $set für Reaktivität
      this.packages.forEach((pkg) => {
        for (const studioId in pkg.studioSettings) {
          const studio = pkg.studioSettings[studioId]
          if (studio.memberships) {
            const membershipIndex = studio.memberships.findIndex((m) => m.title === updatedMembership.title)

            if (membershipIndex > -1) {
              if (isDelete || !updatedMembership.studios.includes(studioId)) {
                studio.memberships.splice(membershipIndex, 1)
                changesMade = true
              } else {
                const sourceDurations = updatedMembership.studioSettings[studioId]?.durations || []
                const targetMembership = studio.memberships[membershipIndex]

                if (!targetMembership.packageDurations) {
                  this.$set(targetMembership, "packageDurations", [])
                }

                // Alte entfernen, die es nicht mehr gibt
                const newDurations = targetMembership.packageDurations.filter((pkgDur) =>
                  sourceDurations.some((srcDur) => srcDur.years === pkgDur.years && srcDur.months === pkgDur.months && srcDur.days === pkgDur.days),
                )

                // Neue hinzufügen
                sourceDurations.forEach((srcDur) => {
                  const exists = newDurations.some(
                    (pkgDur) => srcDur.years === pkgDur.years && srcDur.months === pkgDur.months && srcDur.days === pkgDur.days,
                  )
                  if (!exists) {
                    newDurations.push({ ...srcDur, packagePrice: 0 })
                  }
                })

                // Reaktiv zuweisen
                this.$set(targetMembership, "packageDurations", newDurations)
                changesMade = true
              }
            }
          }
        }
      })

      if (changesMade) {
        try {
          await api.saveContractSettings({ data: this.packages }, "packages")
          this.$toast.info("Zusatzpakete wurden automatisch synchronisiert.")
        } catch (e) {
          this.$toast.error("Fehler bei der Synchronisierung der Zusatzpakete.")
        }
      }
    },
    handlePackagePriceUpdate({ membershipTitle, duration, prices, studioId }) {
      // Hier nutzen wir JSON parse/stringify als einfache Lösung, da es keine tiefe Rekursion erfordert
      const packagesToUpdate = JSON.parse(JSON.stringify(this.packages))
      Object.keys(prices).forEach((packageName) => {
        const pkg = packagesToUpdate.find((p) => p.title === packageName)
        if (pkg && pkg.studioSettings[studioId]) {
          const price = prices[packageName][studioId]
          const membershipSettings = pkg.studioSettings[studioId].memberships.find((m) => m.title === membershipTitle)
          if (membershipSettings && !membershipSettings.flexActive) {
            if (!membershipSettings.packageDurations) {
              membershipSettings.packageDurations = []
            }
            membershipSettings.packageDurations.push({ ...duration, packagePrice: price })
          }
        }
      })
      this.packages = packagesToUpdate
      this.$toast.info("Preise für Zusatzpakete werden synchronisiert...")
    },
    async downloadBackup() {
      this.$toast.info("Backup wird erstellt...")
      const backupData = await api.fetchContractSettings()
      if (backupData) {
        const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: "application/json" })
        const link = document.createElement("a")
        link.href = URL.createObjectURL(blob)
        link.download = "contractSettingsBackup.json"
        link.click()
        URL.revokeObjectURL(link.href)
      } else {
        this.$toast.error("Fehler beim Abrufen der Backup-Daten.")
      }
    },
    importJson(event) {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const importedData = JSON.parse(e.target.result)
          Object.assign(this, importedData)
          this.$toast.success("Backup erfolgreich importiert")
        }
        reader.readAsText(file)
      }
    },
    handleDrop(event) {
      const files = event.dataTransfer.files
      if (files.length > 0) {
        this.$refs.fileInput.files = files
        this.importJson({ target: this.$refs.fileInput })
      }
    },
    async cleanupPackages() {
      if (!confirm("Dieser Vorgang bereinigt die Datenstruktur aller Zusatzpakete. Dies kann nicht rückgängig gemacht werden. Fortfahren?")) {
        return
      }

      this.cleanupLoading = true
      this.$toast.info("Datenstruktur wird aufgeräumt... Dies kann einen Moment dauern.")

      const packagesToClean = JSON.parse(JSON.stringify(this.packages))
      let changesMade = false

      packagesToClean.forEach((pkg) => {
        for (const studioId in pkg.studioSettings) {
          const studioSetting = pkg.studioSettings[studioId]
          if (studioSetting.memberships && Array.isArray(studioSetting.memberships)) {
            studioSetting.memberships.forEach((linkedMembership) => {
              const originalMembership = this.memberships.find((m) => m.title === linkedMembership.title)
              if (originalMembership) {
                const prunedStudioSettings = {
                  [studioId]: originalMembership.studioSettings[studioId],
                }

                if (JSON.stringify(linkedMembership.studioSettings) !== JSON.stringify(prunedStudioSettings)) {
                  linkedMembership.studioSettings = prunedStudioSettings
                  changesMade = true
                }
              }
            })
          }
        }
      })

      if (changesMade) {
        try {
          await api.saveContractSettings({ data: packagesToClean }, "packages")
          this.packages = packagesToClean
          this.$toast.success("Datenstruktur erfolgreich aufgeräumt! Das nächste Backup wird deutlich kleiner sein.")
        } catch (error) {
          this.$toast.error("Fehler beim Speichern der aufgeräumten Daten.")
        }
      } else {
        this.$toast.info("Keine Optimierungen notwendig, die Datenstruktur ist bereits sauber.")
      }

      this.cleanupLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.input-wrapper {
  width: 100%;
  padding: 1rem;
  border: 2px dashed #444;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: var(--v-primary-base);
    background-color: #333;
  }
}
.input-hidden {
  display: none;
}
.input-label {
  text-align: center;
}
</style>
